import React, { useState, useEffect, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { screenshots } from './landingAssets';

// Jun 2026 — Admin-Rechner-Widget: lazy geladen (unterhalb des Folds, kein Layout-Shift)
const AdminCostCalculator = React.lazy(() => import('@/components/landing/AdminCostCalculator'));

const LazyAdminCostSection = () => {
  const ref = React.useRef(null);
  const [load, setLoad] = useState(false);
  useEffect(() => {
    // Geteilte Links (#admin-rechner): SPA scrollt nicht automatisch zum Anker,
    // weil die Section erst nach dem React-Render existiert → manuell springen.
    if (window.location.hash === '#admin-rechner') {
      setLoad(true);
      setTimeout(() => ref.current?.scrollIntoView({ block: 'start' }), 150);
      return undefined;
    }
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setLoad(true); obs.disconnect(); }
    }, { rootMargin: '600px' });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <section id="admin-rechner" ref={ref}
      className="relative z-[2] py-24 bg-[#031a0e] border-b border-emerald-900/30"
      style={load ? undefined : { minHeight: 720 }}
      data-testid="admin-cost-section">
      {load && (
        <Suspense fallback={<div className="min-h-[600px]" />}>
          <AdminCostCalculator />
        </Suspense>
      )}
    </section>
  );
};
import {
  TrendingUp, Calculator, PiggyBank, FileText, Clock, FolderKanban, Scale,
  Check, Upload, ChevronDown, Shield, ArrowRight, Menu, X,
  Video, Handshake, Download, QrCode, Sparkles, MessageSquare,
  BadgeCheck, FileSignature, CheckCircle2, Truck, Mail, ScanLine, Landmark
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Footer from '@/components/Footer';
import PricingTiersBlock from '@/components/pricing/PricingTiersBlock';
import axios from 'axios';
import { useTracking, useScrollTracking } from '@/hooks/useTracking';
import { useSEO } from '@/hooks/useSEO';

const CAL_LINK = 'ivoliq-wilhelm/demo';

// Cal.com embed loader
const useCalEmbed = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (window.Cal) { setLoaded(true); return; }
    (function (C, A, L) {
      let p = function (a, ar) { a.q.push(ar); };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal; let ar = arguments;
        if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; }
        if (ar[0] === L) { const api = function () { p(api, arguments); }; const namespace = ar[1]; api.q = api.q || []; typeof namespace === "string" ? (cal.ns[namespace] = api) && p(api, ar) : p(cal, ar); return; }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");
    window.Cal("init", { origin: "https://cal.com" });
    window.Cal("ui", { styles: { branding: { brandColor: "#059669" } }, hideEventTypeDetails: false, layout: "month_view" });
    setLoaded(true);
  }, []);
  return loaded;
};

const openCalModal = () => {
  if (window.Cal) {
    window.Cal("modal", { calLink: CAL_LINK, config: { layout: "month_view" } });
  }
};

// Scroll-Animation (Intersection Observer)
const useScrollAnimation = () => {
  const ref = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(entry.target); }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, isVisible];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.7s ease, transform 0.7s ease',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const AnimatedCounter = ({ target, suffix = '', prefix = '' }) => {
  const [count, setCount] = React.useState(0);
  const [ref, isVisible] = useScrollAnimation();
  React.useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, target]);
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// Produkt-Screenshots im dunklen Glass-Frame (Hero rechts)
const ProductScreenshots = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    { src: screenshots.finances, label: 'Finanzen', description: 'Einnahmen, Ausgaben & Gewinn im Blick' },
    { src: screenshots.invoices, label: 'Rechnungen', description: 'Rechtssichere Rechnungen in 30 Sek.' },
    { src: screenshots.time_tracking, label: 'Zeiterfassung', description: 'Arbeitszeiten einfach tracken' },
    { src: screenshots.projects, label: 'Projekte', description: 'Projekte & Kunden verwalten' },
  ];
  useEffect(() => {
    const timer = setInterval(() => setCurrentSlide((prev) => (prev + 1) % slides.length), 4000);
    return () => clearInterval(timer);
  }, [slides.length]);
  return (
    <div className="relative" data-testid="product-screenshots">
      {/* Schwebendes NEU-Badge */}
      <div
        className="absolute -top-5 -left-4 sm:-left-8 z-20 flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 shadow-[0_8px_32px_rgba(16,185,129,0.4)]"
        style={{ animation: 'landing-float 5s ease-in-out infinite' }}
        data-testid="hero-email-badge"
      >
        <Mail className="h-4 w-4 text-green-950" />
        <span className="text-xs sm:text-sm font-black text-green-950">NEU: Belegerfassung per E-Mail</span>
      </div>
      <div className="rounded-2xl border border-emerald-800/30 bg-green-950/40 backdrop-blur-xl backdrop-saturate-150 shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">
        <div className="border-b border-emerald-900/40 px-4 py-2.5 flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-400/70"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-400/70"></div>
          <div className="w-3 h-3 rounded-full bg-emerald-400/70"></div>
          <span className="text-xs text-emerald-100/50 ml-2 font-medium">ivoliq.io</span>
        </div>
        <div className="relative aspect-[16/10] overflow-hidden bg-[#062417]">
          {slides.map((slide, index) => (
            <img
              key={index}
              src={slide.src}
              alt={slide.label}
              className={`absolute inset-0 w-full h-full object-cover object-top ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
              style={{ transition: 'opacity 0.7s ease' }}
            />
          ))}
        </div>
        <div className="border-t border-emerald-900/40 px-4 py-3 flex items-center justify-between">
          <div>
            <span className="text-sm font-semibold text-white">{slides[currentSlide].label}</span>
            <span className="text-xs text-emerald-100/50 ml-2 hidden sm:inline">{slides[currentSlide].description}</span>
          </div>
          <div className="flex gap-1.5">
            {slides.map((_, index) => (
              <button
                key={index}
                data-testid={`hero-slide-dot-${index}`}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full ${index === currentSlide ? 'bg-emerald-400 w-6' : 'bg-emerald-800 w-2'}`}
                style={{ transition: 'width 0.3s ease, background-color 0.3s ease' }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full rounded-2xl bg-emerald-500/10"></div>
    </div>
  );
};

// Bento-Zelle (Tetris-Grid)
const BentoCell = ({ icon: Icon, title, desc, badge, className = '', testid, children }) => (
  <div
    data-testid={testid}
    className={`group relative rounded-2xl border border-emerald-900/50 bg-green-950/20 p-8 overflow-hidden hover:border-emerald-700/60 hover:-translate-y-1 ${className}`}
    style={{ transition: 'border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease', boxShadow: '0 4px 24px rgba(0,0,0,0.25)' }}
  >
    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-700/40 mb-5">
      <Icon className="h-5 w-5 text-emerald-400" />
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <h3 className="font-heading text-lg font-bold text-white">{title}</h3>
      {badge && <span className="text-[10px] font-black bg-emerald-500 text-green-950 px-2 py-0.5 rounded-full">{badge}</span>}
    </div>
    <p className="mt-2 text-sm text-emerald-50/70 leading-relaxed font-display">{desc}</p>
    {children}
  </div>
);

// Exit-Intent-Modal: letztes Lifetime-Angebot beim Verlassen der Seite
const EXIT_OFFER_KEY = 'ivoliq_exit_offer_seen_at';
const EXIT_OFFER_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 Tage
const GUEST_CONSENT_KEY = 'ivoliq_guest_consent_v1'; // einmalige DSGVO-Zustimmung pro Gerät

const ExitIntentOffer = ({ lifetimeBundleInfo, trackEvent, trackPricingClick }) => {
  const [show, setShow] = useState(false);
  const sold = lifetimeBundleInfo?.sold_count || 0;
  const available = !!lifetimeBundleInfo && sold < 150;
  const price = sold < 50 ? 199 : 249;
  const remaining = sold < 50 ? 50 - sold : 150 - sold;

  useEffect(() => {
    if (!available) return;
    try {
      const seenAt = parseInt(localStorage.getItem(EXIT_OFFER_KEY) || '0', 10);
      if (seenAt && Date.now() - seenAt < EXIT_OFFER_COOLDOWN_MS) return;
    } catch (e) { /* Storage blockiert → einfach anzeigen */ }

    let armed = false;
    const onMouseOut = (e) => {
      // Nur echtes Verlassen nach oben (Richtung Tab-Leiste / URL-Zeile)
      if (!armed || e.clientY > 0 || e.relatedTarget) return;
      setShow(true);
      try { localStorage.setItem(EXIT_OFFER_KEY, String(Date.now())); } catch (err) {}
      trackEvent('exit_intent_shown', { offer: 'lifetime', price, remaining });
      document.removeEventListener('mouseout', onMouseOut);
    };
    // Erst nach 8 s scharf schalten — kein Popup für Sofort-Abspringer
    const timer = setTimeout(() => { armed = true; }, 8000);
    document.addEventListener('mouseout', onMouseOut);
    return () => { clearTimeout(timer); document.removeEventListener('mouseout', onMouseOut); };
  }, [available, price, remaining, trackEvent]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show]);

  const dismiss = () => {
    setShow(false);
    trackEvent('exit_intent_dismissed', { offer: 'lifetime' });
  };

  const accept = () => {
    trackEvent('exit_intent_cta_click', { offer: 'lifetime', price });
    trackPricingClick('Lifetime', price);
    window.location.href = '/signup?plan=lifetime&intent=lifetime&src=exit';
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
      data-testid="exit-intent-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={dismiss}
        data-testid="exit-intent-backdrop"
        style={{ animation: 'landing-fade-in 0.25s ease' }}
      />
      {/* Karte */}
      <div
        className="relative w-full max-w-lg rounded-2xl border border-emerald-600/50 bg-[#062417] p-8 sm:p-10 overflow-hidden"
        style={{ boxShadow: '0 0 0 2px rgba(16,185,129,0.4), 0 24px 80px rgba(0,0,0,0.6)', animation: 'landing-pop-in 0.3s ease' }}
      >
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 p-2 rounded-full text-emerald-100/50 hover:text-white hover:bg-white/10"
          style={{ transition: 'color 0.2s ease, background-color 0.2s ease' }}
          aria-label="Schließen"
          data-testid="exit-intent-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-3.5 py-1.5 mb-6">
            <Sparkles className="h-4 w-4 text-green-950" />
            <span className="text-xs font-black text-green-950 uppercase tracking-wide">Nur noch {remaining} verfügbar</span>
          </div>
          <h3 className="font-heading text-3xl sm:text-4xl text-white tracking-tight leading-[1.05]">
            <span className="font-light">Warte kurz —</span>{' '}
            <span className="font-black text-emerald-400">einmal zahlen, für immer nutzen.</span>
          </h3>
          <p className="mt-4 text-emerald-50/70 leading-relaxed">
            Sichere dir den <strong className="text-white">ivoliq Lifetime-Deal</strong>: alle Pro-Features,
            alle künftigen Updates — ohne Abo, ohne monatliche Kosten. Für immer.
          </p>
          <div className="mt-6 flex items-end gap-3">
            <span className="font-heading text-5xl font-black text-white" data-testid="exit-intent-price">{price} €</span>
            <span className="text-sm text-emerald-100/50 pb-2">einmalig · statt 19 €/Monat</span>
          </div>
          <ul className="mt-6 space-y-2.5">
            {['Alle Pro-Features inklusive', 'Alle zukünftigen Updates', 'KI-Belegerfassung inkl. E-Mail-Inbox', 'Steuerberater-Portal & DATEV-Export'].map((item) => (
              <li key={item} className="flex items-center gap-2.5 text-sm text-emerald-50/80">
                <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={accept}
              className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-green-950 font-bold px-8 py-6 text-base shadow-[0_8px_32px_rgba(16,185,129,0.35)] flex-1"
              style={{ transition: 'background-color 0.2s ease' }}
              data-testid="exit-intent-cta"
            >
              Lifetime-Deal sichern <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <button
              onClick={dismiss}
              className="text-sm text-emerald-100/50 hover:text-white px-4 py-3"
              style={{ transition: 'color 0.2s ease' }}
              data-testid="exit-intent-decline"
            >
              Nein danke
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function NewLandingOptimized() {
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeCountry, setActiveCountry] = useState('DE');
  const [plansData, setPlansData] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [lifetimeBundleInfo, setLifetimeBundleInfo] = useState(null);
  const { trackEvent, trackCTAClick, trackFAQOpen, trackPricingClick } = useTracking();
  const navigate = useNavigate();
  const { startGuest } = useAuth();
  const [guestStarting, setGuestStarting] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const doStartGuest = async () => {
    if (guestStarting) return;
    setGuestStarting(true);
    try {
      trackCTAClick('hero', 'Jetzt ausprobieren');
      await startGuest('DE');
      navigate('/willkommen');
    } catch {
      toast.error('Der Demo-Start hat nicht geklappt. Bitte versuche es erneut.');
      setGuestStarting(false);
    }
  };

  // Vor dem Start: einmalige DSGVO-Zustimmung (Gast-Zugang, Löschung nach 3 Tagen).
  const handleTryGuest = () => {
    if (guestStarting) return;
    let consented = false;
    try { consented = localStorage.getItem(GUEST_CONSENT_KEY) === '1'; } catch { /* ignore */ }
    if (consented) { doStartGuest(); return; }
    setConsentChecked(false);
    setConsentOpen(true);
  };

  const confirmConsentAndStart = () => {
    if (!consentChecked) return;
    try { localStorage.setItem(GUEST_CONSENT_KEY, '1'); } catch { /* ignore */ }
    setConsentOpen(false);
    doStartGuest();
  };

  useSEO({
    title: 'ivoliq – Die Admin-Plattform für Freelancer & Solopreneure | DE, AT & CH',
    description: 'Zeiterfassung, Angebote mit Online-Annahme, Rechnungen mit Zahlungs-QR, KI-Belege, DATEV-Export und Steuerberater-Portal – alles in einer Plattform. Rechtssicher in DE, AT & CH. Kostenlos starten.',
    canonical: 'https://ivoliq.io/',
    robots: 'index,follow'
  });

  useScrollTracking();
  useCalEmbed();

  useEffect(() => {
    if (window.gtag) {
      const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-8Q9R1YT6JP';
      window.gtag('config', GA_MEASUREMENT_ID, { page_path: '/', page_title: 'ivoliq Landing Page - Optimized' });
    }
  }, []);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${API}/api/plans`).then(r => setPlansData(r.data)).catch(() => {});
    axios.get(`${API}/api/lifetime-bundle-info`).then(r => setLifetimeBundleInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const ogTags = {
      'og:title': 'ivoliq – Die Admin-Plattform für Freelancer & Solopreneure | DE, AT & CH',
      'og:description': 'Zeiterfassung, Rechnungen mit Zahlungs-QR, KI-Belege, DATEV-Export und Steuerberater-Portal – alles in einer Plattform.',
      'og:type': 'website',
      'og:url': 'https://ivoliq.io',
      'og:site_name': 'ivoliq',
      'og:locale': 'de_DE',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'ivoliq – Admin-Plattform für Freelancer & Solopreneure',
      'twitter:description': 'Zeiterfassung, Rechnungen, KI-Belege, DATEV-Export & Steuerberater-Portal – alles in einer Plattform.'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      const isOg = property.startsWith('og:');
      const selector = isOg ? `meta[property="${property}"]` : `meta[name="${property}"]`;
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (isOg) meta.setAttribute('property', property);
        else meta.setAttribute('name', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    });
  }, []);

  const toggleFaq = (index, question) => {
    setOpenFaq(openFaq === index ? null : index);
    if (openFaq !== index) trackFAQOpen(question);
  };

  const faqItems = [
    { id: 0, q: 'Brauche ich eine Kreditkarte zum Testen?', a: <>Nein. Der Free Plan ist 100% kostenlos und ohne Kreditkarte. Beim Pro Plan kannst du 30 Tage kostenlos testen – dafür ist eine Kreditkarte erforderlich, es wird aber erst nach der Testphase abgebucht.</> },
    { id: 1, q: 'Funktioniert ivoliq auch für die Schweiz?', a: <>Ja! ivoliq unterstützt DE, AT und CH mit allen rechtlichen Anforderungen. Swiss QR-Code, UID-Nummern, SVS-Berechnung werden unterstützt.</> },
    { id: 12, q: 'Muss ich mich mit ELSTER, DATEV oder UVA auskennen?', a: <>Nein. ivoliq spricht die Behördensprache für dich: Voranmeldungen, Exporte und Formate werden automatisch richtig erstellt. Du musst keinen dieser Begriffe kennen — dein Finanzamt und dein Steuerberater bekommen trotzdem genau das, was sie brauchen.</>, testid: 'faq-jargon' },
    { id: 2, q: 'Kann ich meine Daten exportieren (inkl. DATEV)?', a: <>Ja, jederzeit. CSV für Excel, <strong className="text-white">DATEV-CSV</strong> für deinen Steuerberater (Pro), EÜR-PDF oder ein ZIP mit allen Belegen inklusive Excel-Übersicht. Deine Daten gehören dir – kein Lock-in.</> },
    { id: 10, q: 'Kann ich meinen Steuerberater direkt anbinden?', a: <>Ja. ivoliq hat ein eigenes <Link to="/steuerberater-portal" className="text-emerald-400 underline">Steuerberater-Portal</Link>. Du lädst deinen Steuerberater per E-Mail ein, er bekommt einen eigenen Login und sieht genau die Daten, die du freigibst. Kommentare, Aufgaben, Perioden-Freigaben und DATEV-Export sind integriert – DSGVO-konform mit Servern in der EU.</>, testid: 'faq-sb-portal' },
    { id: 11, q: 'Bekommen meine Rechnungen einen Zahlungs-QR-Code?', a: <>Ja. Im Pro-Plan drucken wir automatisch einen <strong className="text-white">GiroCode (EPC-QR)</strong> für DE und AT sowie <strong className="text-white">Swiss QR-Bill</strong> für die Schweiz auf jede Rechnung. Deine Kunden scannen den QR-Code mit ihrer Banking-App – Empfänger, IBAN, Betrag und Verwendungszweck sind automatisch ausgefüllt. Weniger Tippen, weniger Fehler, schneller bezahlt.</>, testid: 'faq-qr-code' },
    { id: 3, q: 'Ersetzt ivoliq meinen Steuerberater?', a: <>Nein. ivoliq bereitet deine Buchhaltung vor und berechnet deine Steuerlast. Deinen Steuerberater unterstützt du damit – ersetzen tut ivoliq ihn nicht.</> },
    { id: 4, q: 'Wie sicher sind meine Daten?', a: <>Sehr sicher. Alle Daten werden in der EU gehostet, verschlüsselt übertragen und sind DSGVO-konform. Wir verkaufen keine Daten.</> },
    { id: 5, q: 'Wird ivoliq weiterentwickelt?', a: <>Ja! Wir entwickeln ivoliq kontinuierlich weiter und fügen regelmäßig neue Features hinzu. <strong className="text-white">Wichtig: Dein Preis bleibt ab dem Zeitpunkt deiner Anmeldung garantiert gleich</strong> – auch wenn wir neue Features hinzufügen oder Preise für Neukunden anpassen.</> },
  ];

  const faqSchemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Brauche ich eine Kreditkarte zum Testen?", "acceptedAnswer": { "@type": "Answer", "text": "Nein. Der Free Plan ist 100% kostenlos und ohne Kreditkarte. Beim Pro Plan kannst du 30 Tage kostenlos testen – dafür ist eine Kreditkarte erforderlich, es wird aber erst nach der Testphase abgebucht." } },
      { "@type": "Question", "name": "Funktioniert ivoliq auch für die Schweiz?", "acceptedAnswer": { "@type": "Answer", "text": "Ja! ivoliq unterstützt DE, AT und CH mit allen rechtlichen Anforderungen. Swiss QR-Code, UID-Nummern, SVS-Berechnung werden unterstützt." } },
      { "@type": "Question", "name": "Muss ich mich mit ELSTER, DATEV oder UVA auskennen?", "acceptedAnswer": { "@type": "Answer", "text": "Nein. ivoliq spricht die Behördensprache für dich: Voranmeldungen, Exporte und Formate werden automatisch richtig erstellt. Du musst keinen dieser Begriffe kennen — dein Finanzamt und dein Steuerberater bekommen trotzdem genau das, was sie brauchen." } },
      { "@type": "Question", "name": "Kann ich meine Daten exportieren?", "acceptedAnswer": { "@type": "Answer", "text": "Ja, jederzeit. Als CSV für Excel, als DATEV-CSV für deinen Steuerberater (Pro) oder als EÜR-PDF. Zusätzlich gibt es einen ZIP-Export mit allen Belegen inkl. Excel-Übersicht. Deine Daten gehören dir – kein Lock-in." } },
      { "@type": "Question", "name": "Kann ich meinen Steuerberater direkt anbinden?", "acceptedAnswer": { "@type": "Answer", "text": "Ja. ivoliq hat ein eigenes Steuerberater-Portal. Du lädst deinen Steuerberater per E-Mail ein, er bekommt einen eigenen Login und sieht genau die Daten, die du freigibst. Kommentare, Aufgaben, Perioden-Freigaben und DATEV-Export sind integriert – DSGVO-konform mit Servern in der EU." } },
      { "@type": "Question", "name": "Bekommen meine Rechnungen einen Zahlungs-QR-Code?", "acceptedAnswer": { "@type": "Answer", "text": "Ja. Im Pro-Plan drucken wir automatisch einen GiroCode (EPC-QR) für DE und AT sowie Swiss QR-Bill für die Schweiz auf jede Rechnung. Deine Kunden scannen den QR-Code mit ihrer Banking-App – Empfänger, IBAN, Betrag und Verwendungszweck sind automatisch ausgefüllt. Weniger Tippen, weniger Fehler, schneller bezahlt." } },
      { "@type": "Question", "name": "Ersetzt ivoliq meinen Steuerberater?", "acceptedAnswer": { "@type": "Answer", "text": "Nein. ivoliq bereitet deine Buchhaltung vor und berechnet deine Steuerlast. Deinen Steuerberater unterstützt du damit – ersetzen tut ivoliq ihn nicht." } },
      { "@type": "Question", "name": "Wie sicher sind meine Daten?", "acceptedAnswer": { "@type": "Answer", "text": "Sehr sicher. Alle Daten werden in der EU gehostet, verschlüsselt übertragen und sind DSGVO-konform. Wir verkaufen keine Daten." } },
      { "@type": "Question", "name": "Wird ivoliq weiterentwickelt?", "acceptedAnswer": { "@type": "Answer", "text": "Ja! Wir entwickeln ivoliq kontinuierlich weiter und fügen regelmäßig neue Features hinzu. Dein Preis bleibt ab dem Zeitpunkt deiner Anmeldung garantiert gleich." } }
    ]
  };

  const trustLogos = ['DATEV', 'ELSTER', 'FinanzOnline', 'BMD-NTCS', 'XRechnung & ZUGFeRD', 'Swiss QR-Bill', 'GoBD', 'DSGVO · EU-Server'];
  const countries = [
    { code: 'DE', flag: '🇩🇪', name: 'Deutschland' },
    { code: 'AT', flag: '🇦🇹', name: 'Österreich' },
    { code: 'CH', flag: '🇨🇭', name: 'Schweiz' },
  ];
  const countryFeatures = {
    DE: [
      { t: 'ELSTER-UStVA', d: 'Umsatzsteuer-Voranmeldung vorbereitet fürs ELSTER-Portal – Kennzahlen automatisch berechnet.' },
      { t: 'Einkommensteuer §32a', d: 'Progressiver Tarif inkl. Grundfreibetrag, Soli & Kirchensteuer automatisch.' },
      { t: 'EÜR & GoBD', d: 'Einnahmen-Überschuss-Rechnung, alle Pflichtangaben nach §14 UStG, GoBD-konforme Exporte.' },
      { t: 'E-Rechnung: ZUGFeRD & XRechnung', d: 'Jede PDF-Rechnung mit eingebettetem ZUGFeRD-XML — bereit für die E-Rechnungspflicht 2027/2028. XRechnung-Export für Behörden, Empfang eingehender E-Rechnungen inklusive.' },
    ],
    AT: [
      { t: 'FinanzOnline-UVA', d: 'UVA-Kennzahlen inkl. Reverse Charge automatisch berechnet, XML-Export für FinanzOnline.' },
      { t: 'SVS-Berechnung', d: 'Pensions-, Kranken- & Unfallversicherung der Selbständigen laufend prognostiziert.' },
      { t: 'Gewinnfreibetrag §10', d: 'Grundfreibetrag + Investitionsfreibetrag mit interaktivem Regler.' },
      { t: 'UID & Pflichtangaben', d: 'UID-Validierung und alle österreichischen Pflichtangaben auf Rechnungen.' },
    ],
    CH: [
      { t: 'MWST 8.1%', d: 'Schweizer Mehrwertsteuer inkl. reduziertem Satz & Befreiungen.' },
      { t: 'Swiss QR-Rechnung', d: 'QR-Bill auf jeder Rechnung – Standard für den Schweizer Zahlungsverkehr.' },
      { t: 'Bundes- & Kantonalsteuer', d: 'Direkte Bundessteuer plus Kantons- und Gemeindesteuer nach Standort.' },
      { t: 'CHF & EUR', d: 'Rechnungen in beiden Währungen – perfekt für grenzüberschreitende Projekte.' },
    ],
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#021c10] font-display" data-testid="landing-page-root">

      {/* Landing-spezifische Keyframes & Tracing-Beam (scoped) */}
      <style>{`
        @keyframes landing-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes landing-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes landing-beam { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        .landing-tracing-beam [data-testid="lifetime-banner"] {
          position: relative;
          border: 2px solid transparent;
          background-clip: padding-box;
          box-shadow: 0 0 0 2px rgba(16,185,129,0.55), 0 0 32px rgba(16,185,129,0.25);
          animation: landing-beam 4s ease infinite;
        }
      `}</style>

      {/* Grain-Textur (fixiert, über der gesamten Seite) */}
      <div
        className="pointer-events-none fixed inset-0 z-[1]"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")` }}
      />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "ivoliq",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": "Buchhaltungslösung für Freelancer: Zeiterfassung, Rechnungen erstellen, Steuern berechnen – alles in einer Plattform für DE, AT & CH.",
        "url": "https://ivoliq.io",
        "offers": [
          { "@type": "Offer", "price": "0", "priceCurrency": "EUR", "name": "Free Plan", "description": "Kostenloser Plan mit Zeiterfassung und PDF-Rechnungen" },
          { "@type": "Offer", "price": "19", "priceCurrency": "EUR", "name": "Pro Plan", "description": "Vollversion mit automatischen Rechnungen, Dashboard und Steuerberechnung", "priceSpecification": { "@type": "UnitPriceSpecification", "price": "19", "priceCurrency": "EUR", "billingDuration": "P1M" } }
        ]
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "Organization", "name": "ivoliq", "url": "https://ivoliq.io",
        "description": "Buchhaltung für Freelancer in Deutschland, Österreich und der Schweiz", "sameAs": []
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchemaData) }} />

      {/* STICKY NAV — Crystal Glass */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 bg-green-950/40 backdrop-blur-xl backdrop-saturate-150 border-b border-emerald-800/30 ${scrolled ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : ''}`}
        style={{ transition: 'box-shadow 0.3s ease' }}
        data-testid="landing-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <span className="font-heading text-2xl font-black text-white tracking-tight">ivoliq<span className="text-emerald-400">.</span></span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-emerald-50/70 hover:text-white text-sm font-medium" style={{ transition: 'color 0.2s ease' }}>Features</a>
              <a href="#pricing" className="text-emerald-50/70 hover:text-white text-sm font-medium" style={{ transition: 'color 0.2s ease' }}>Preise</a>
              <a href="#faq" className="text-emerald-50/70 hover:text-white text-sm font-medium" style={{ transition: 'color 0.2s ease' }}>FAQ</a>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <button onClick={openCalModal} className="text-emerald-50/70 hover:text-emerald-300 text-sm font-medium" style={{ transition: 'color 0.2s ease' }} data-testid="nav-demo-btn">
                Demo buchen
              </button>
              <Link to="/login" className="text-emerald-50/70 hover:text-white text-sm font-medium" style={{ transition: 'color 0.2s ease' }} data-testid="nav-login-link">
                Login
              </Link>
              <Link to="/signup" data-testid="nav-signup-link">
                <Button className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-green-950 font-bold px-6" data-testid="nav-signup-btn">
                  Kostenlos starten
                </Button>
              </Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="nav-mobile-toggle">
              {mobileMenuOpen ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#062417] border-t border-emerald-900/40" data-testid="nav-mobile-menu">
            <div className="px-4 py-6 space-y-4">
              <a href="#features" className="block text-emerald-50/80" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#pricing" className="block text-emerald-50/80" onClick={() => setMobileMenuOpen(false)}>Preise</a>
              <a href="#faq" className="block text-emerald-50/80" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
              <hr className="border-emerald-900/50" />
              <Link to="/login" className="block text-emerald-50/80" onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <button onClick={() => { setMobileMenuOpen(false); openCalModal(); }} className="block text-emerald-400 font-medium">Demo buchen</button>
              <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-green-950 font-bold">Kostenlos starten</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* 1. HERO — asymmetrisch, dunkles Waldgrün */}
      <section className="relative overflow-hidden pt-32 pb-24 sm:pt-40 sm:pb-32" data-testid="hero-section">
        <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/4 h-[320px] w-[320px] rounded-full bg-teal-400/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-[2]">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center">
            {/* Links: Typografie + CTAs */}
            <div className="space-y-10">
              <div className="space-y-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-800/40 bg-green-950/40 backdrop-blur-md px-4 py-1.5 text-sm font-semibold text-emerald-300">
                  <Sparkles className="h-4 w-4" />
                  Made in DACH · für Freelancer &amp; Solopreneure
                </div>
                <h1 className="font-heading text-4xl sm:text-6xl text-white leading-[1.05] sm:leading-[1.02] tracking-tight break-words hyphens-auto" data-testid="hero-headline">
                  <span className="font-light">Buchhaltung, die sich</span>{' '}
                  <span className="font-black text-emerald-400">fast von selbst erledigt.</span>
                </h1>
                <p className="text-lg sm:text-xl text-emerald-50/70 leading-relaxed max-w-xl">
                  Beleg abfotografieren oder einfach weiterleiten — fertig. ivoliq{' '}
                  <strong className="text-white font-semibold">verbucht</strong>,{' '}
                  <strong className="text-white font-semibold">rechnet deine Steuern</strong> und zeigt dir jeden Tag,{' '}
                  <strong className="text-white font-semibold">was dir wirklich bleibt</strong>.
                  Für Freelancer &amp; Solopreneure in DE, AT &amp; CH.
                </p>
              </div>
              <div className="text-center">
                <div
                  className="relative pill-shine flex flex-col sm:flex-row w-full sm:w-auto sm:inline-flex items-stretch rounded-2xl sm:rounded-full overflow-hidden shadow-[0_8px_32px_rgba(16,185,129,0.35)]"
                  data-testid="hero-split-cta"
                >
                  <Link
                    to="/signup"
                    onClick={() => trackCTAClick('hero', 'Kostenlos starten')}
                    data-testid="hero-signup-link"
                    className="flex-1 sm:flex-none flex"
                  >
                    <Button
                      size="lg"
                      className="w-full h-auto rounded-none bg-emerald-700 hover:bg-emerald-600 text-white px-6 sm:px-9 py-4 border-0 flex flex-col items-center gap-0.5 leading-tight"
                      style={{ transition: 'background-color 0.2s ease' }}
                      data-testid="hero-signup-button"
                    >
                      <span className="font-bold text-base sm:text-lg">Kostenlos starten</span>
                      <span className="text-xs font-medium text-emerald-100/80">Konto anlegen &amp; loslegen</span>
                    </Button>
                  </Link>
                  <div className="h-px w-full sm:h-auto sm:w-px bg-white/15 shrink-0" aria-hidden="true" />
                  <Button
                    size="lg"
                    onClick={handleTryGuest}
                    disabled={guestStarting}
                    className="flex-1 sm:flex-none h-auto rounded-none bg-neutral-950 hover:bg-neutral-800 text-white px-6 sm:px-9 py-4 border-0 flex flex-col items-center gap-0.5 leading-tight"
                    style={{ transition: 'background-color 0.2s ease' }}
                    data-testid="hero-try-guest-button"
                  >
                    <span className="font-bold text-base sm:text-lg">{guestStarting ? 'Wird geladen…' : 'Jetzt ausprobieren'}</span>
                    {!guestStarting && (
                      <span className="text-xs font-medium text-white/60">Demo — ohne Anmeldung</span>
                    )}
                  </Button>
                </div>
                <div className="mt-3">
                  <a
                    href="#pricing"
                    onClick={() => trackCTAClick('hero', 'Preise ansehen')}
                    data-testid="hero-pricing-link"
                    className="text-sm font-medium text-emerald-200/70 hover:text-emerald-100 underline underline-offset-4"
                    style={{ transition: 'color 0.2s ease' }}
                  >
                    Preise ansehen
                  </a>
                </div>
                <p className="text-sm text-emerald-100/50 mt-4" data-testid="hero-trust-line">
                  ✓ Free Plan ohne Kreditkarte &nbsp;·&nbsp; ✓ 30 Tage Pro kostenlos testen &nbsp;·&nbsp; ✓ Jederzeit kündbar
                </p>
                <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
                  <DialogContent className="max-w-md" data-testid="guest-consent-dialog">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-emerald-600" />
                        Kurz zur Demo
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-sm text-slate-600">
                      <p>
                        Du startest einen unverbindlichen <strong className="text-slate-900">Gast-Zugang</strong> mit
                        Beispieldaten — ganz ohne Anmeldung.
                      </p>
                      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-amber-900">
                        Alle Daten dieses Gast-Zugangs werden nach <strong>3 Tagen</strong> automatisch gelöscht.
                        Lege in dieser Zeit ein <strong>kostenloses Konto</strong> an — dann werden deine eigenen
                        Inhalte (Kunden, Projekte, Rechnungen) automatisch übernommen.
                      </div>
                      <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="guest-consent-label">
                        <input
                          type="checkbox"
                          checked={consentChecked}
                          onChange={(e) => setConsentChecked(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          data-testid="guest-consent-checkbox"
                        />
                        <span>
                          Ich habe verstanden, dass es sich um einen Gast-Zugang handelt und die Daten nach 3 Tagen
                          gelöscht werden, falls ich kein Konto anlege.
                        </span>
                      </label>
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
                      <Button
                        variant="outline"
                        onClick={() => setConsentOpen(false)}
                        data-testid="guest-consent-cancel"
                      >
                        Abbrechen
                      </Button>
                      <Button
                        onClick={confirmConsentAndStart}
                        disabled={!consentChecked || guestStarting}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"
                        data-testid="guest-consent-confirm"
                      >
                        {guestStarting ? 'Wird geladen…' : 'Verstanden, Demo starten'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3 pt-2">
                {[
                  { icon: PiggyBank, label: 'Rücklage immer im Blick' },
                  { icon: QrCode, label: 'Schneller bezahlt' },
                  { icon: Handshake, label: 'Steuerberater-fertig' },
                  { icon: Shield, label: 'DSGVO · EU-Server' },
                ].map(({ icon: I, label }) => (
                  <div key={label} className="flex items-center gap-2">
                    <I className="h-4 w-4 text-emerald-400" />
                    <span className="text-xs font-medium text-emerald-50/60">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Rechts: Schwebendes Produkt-Mockup */}
            <div className="relative lg:translate-x-4">
              <ProductScreenshots />
            </div>
          </div>
        </div>
      </section>

      {/* 2. SOCIAL PROOF — Editorial Ribbon Marquee */}
      <section className="relative z-[2] border-y border-emerald-900/30 py-8 overflow-hidden" data-testid="social-proof-ribbon">
        <p className="text-center text-xs font-semibold uppercase tracking-widest text-emerald-100/40 mb-5 px-4">
          <span className="normal-case">ivoliq</span> spricht die Sprache deines Finanzamts — damit du es nicht musst:
        </p>
        <div className="flex whitespace-nowrap" style={{ animation: 'landing-marquee 45s linear infinite', width: 'max-content' }}>
          {[...trustLogos, ...trustLogos].map((logo, i) => (
            <span key={i} className="font-heading text-xl font-bold text-emerald-100/30 tracking-widest uppercase mx-12 sm:mx-16">
              {logo}
            </span>
          ))}
        </div>
      </section>

      {/* ADMIN-RECHNER — „Welcher Freelancer willst du sein?" (lazy, #admin-rechner) */}
      <LazyAdminCostSection />

      {/* 3-OUTCOME-BLOCK — was sich ab Tag 1 ändert */}
      <section className="relative z-[2] py-20" data-testid="outcomes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <p className="text-sm font-semibold text-emerald-400 uppercase tracking-widest mb-10" data-testid="outcomes-eyebrow">
              500+ Freelancer &amp; Solopreneure nutzen <span className="normal-case">ivoliq</span> — das ändert sich ab Tag 1:
            </p>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {[
                { icon: Clock, t: <>Bis zu <AnimatedCounter target={10} suffix=" Stunden" /> pro Monat zurück</>, d: 'Belege, Rechnungen, Ablage — läuft im Hintergrund. Du machst dein eigentliches Business.', testid: 'outcome-time' },
                { icon: PiggyBank, t: 'Nie wieder Steuer-Schock', d: 'Du siehst jeden Tag, was du zurücklegen musst — und was dir nach Steuern wirklich bleibt.', testid: 'outcome-tax' },
                { icon: TrendingUp, t: 'Schneller bezahlt werden', d: 'Getrackte Stunden werden mit einem Klick zur Rechnung — mit Zahlungs-QR, den dein Kunde einfach scannt.', testid: 'outcome-paid' },
              ].map(({ icon: I, t, d, testid }) => (
                <div key={testid} data-testid={testid} className="rounded-2xl border border-emerald-900/50 bg-green-950/20 p-8" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.25)' }}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-700/40 mb-5">
                    <I className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h3 className="font-heading text-xl font-black text-white">{t}</h3>
                  <p className="mt-2.5 text-sm text-emerald-50/60 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* WAS DU NIE WIEDER TUN MUSST */}
      <section className="relative z-[2] py-24 bg-[#062417]" data-testid="never-again-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-center">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">
                  <span className="font-light">Was du</span>{' '}
                  <span className="font-black">nie wieder tun musst.</span>
                </h2>
                <p className="mt-4 text-base md:text-lg text-emerald-50/60 leading-relaxed">
                  ivoliq übernimmt die Arbeit im Hintergrund — <strong className="text-white">du bestätigst nur noch.</strong>
                </p>
              </div>
              <ul className="space-y-4">
                {[
                  'Sonntagabends Belege in Mail-Postfächern und Schubladen suchen',
                  'Excel-Listen pflegen, die im Dezember niemand mehr versteht',
                  'Rätseln, wie viel du für Steuern zur Seite legen musst',
                  'Rechnungen von Hand tippen und Zahlungen hinterherlaufen',
                  'Mit Schuhkarton und schlechtem Gewissen zum Steuerberater',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3.5" data-testid="never-again-item">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500/10 border border-red-500/20 flex-shrink-0 mt-0.5">
                      <X className="h-3.5 w-3.5 text-red-400/80" />
                    </span>
                    <span className="text-emerald-50/60 line-through decoration-emerald-50/30 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 3. BENTO-GRID — Pain vs. Solution als Tetris-Grid */}
      <section id="features" className="relative z-[2] py-24 sm:py-32" data-testid="bento-features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-3xl mb-16">
              <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">
                <span className="font-light">Schluss mit Excel-Chaos.</span>{' '}
                <span className="font-black">Ein Werkzeug für alles.</span>
              </h2>
              <p className="mt-4 text-base md:text-lg text-emerald-50/60 leading-relaxed">
                Keine 10 Tools mehr, keine verlorenen Belege, keine bösen Überraschungen beim Steuerberater.
                ivoliq automatisiert deinen kompletten Workflow — von der ersten Arbeitsstunde bis zur Steuererklärung.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 sm:auto-rows-fr">
              {/* Hero-Zelle: Steuern DACH (2x2) */}
              <div
                data-testid="bento-tax-dach"
                className="sm:col-span-2 lg:row-span-2 relative rounded-2xl border border-emerald-700/50 bg-[#062417] p-8 sm:p-10 overflow-hidden hover:-translate-y-1"
                style={{ transition: 'transform 0.3s ease, border-color 0.3s ease', boxShadow: '0 8px 40px rgba(0,0,0,0.35)' }}
              >
                <div className="pointer-events-none absolute -top-20 -right-20 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 border border-emerald-600/40 mb-6">
                  <Landmark className="h-6 w-6 text-emerald-400" />
                </div>
                <h3 className="font-heading text-2xl font-black text-white">Wissen, was dir wirklich bleibt.</h3>
                <p className="mt-3 text-emerald-50/70 leading-relaxed">
                  Einnahmen rein — ivoliq berechnet laufend Steuern, Abgaben und deine nötige Rücklage.
                  Du siehst jeden Tag deinen echten Gewinn. Für DE, AT &amp; CH bis ins Detail integriert:
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {['ELSTER UStVA', 'FinanzOnline UVA', 'SVS-Prognose', 'EÜR-Export', 'Gewerbesteuer', 'MWST 8.1%'].map(t => (
                    <span key={t} className="rounded-full border border-emerald-800/50 bg-green-950/40 px-3 py-1 text-xs font-medium text-emerald-200">{t}</span>
                  ))}
                </div>
                <div className="mt-8 rounded-xl border border-emerald-900/50 bg-green-950/40 p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-emerald-50/60">Monatliche Steuerrücklage</span>
                    <span className="font-heading font-black text-emerald-400 text-lg">1.240 €</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-emerald-950 overflow-hidden">
                    <div className="h-full w-2/3 rounded-full bg-emerald-500" />
                  </div>
                  <p className="mt-2 text-xs text-emerald-100/40">ESt + SVS + USt — automatisch prognostiziert</p>
                </div>
              </div>

              <BentoCell
                icon={ScanLine}
                title="Beleg weiterleiten. Fertig."
                badge="NEU: per E-Mail"
                desc="Foto, Drag & Drop oder E-Mail an deine persönliche Beleg-Adresse — die KI verbucht für dich."
                testid="bento-receipts"
              />
              <BentoCell
                icon={FileText}
                title="Rechnung in 30 Sek."
                desc="Rechtssicher, mit Zahlungs-QR — deine Kunden zahlen per Scan statt Überweisungs-Tipperei."
                testid="bento-invoices"
              />
              <BentoCell
                icon={Clock}
                title="Keine Stunde geht verloren"
                desc="One-Click-Timer mit Projektzuordnung — abrechenbare Zeit landet automatisch auf der Rechnung."
                testid="bento-time"
              />
              <BentoCell
                icon={Handshake}
                title="Dein StB bekommt alles — ohne dass du sortierst"
                desc="Eigener Login für deinen Steuerberater: vorsortierte Belege, Freigaben und DATEV-Export auf Knopfdruck."
                testid="bento-advisor"
              />
              <BentoCell
                icon={Truck}
                title="In Minuten umgezogen"
                desc="Von sevDesk, Lexware, DATEV oder Excel — KI-gestützt, ohne Copy-Paste, ohne Migrationsgebühr."
                testid="bento-migration"
              />
              <BentoCell
                icon={FileSignature}
                title="Vom Angebot zur Rechnung in 1 Klick"
                desc="Kunde nimmt per Link an oder lehnt ab — ivoliq macht daraus automatisch die Rechnung."
                testid="bento-quotes"
              />
              <BentoCell
                icon={Download}
                title="Deine Daten gehören dir"
                desc="DATEV-CSV, BMD-NTCS, ZIP-Belegbundle, Excel & EÜR-PDF — jederzeit raus, kein Lock-in."
                testid="bento-export"
              />
              <BentoCell
                icon={FolderKanban}
                title="Wissen, welche Kunden sich lohnen"
                desc="Budgets, Stundensätze und Profitabilität pro Kunde und Projekt auf einen Blick."
                testid="bento-projects"
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 4. DEEP DIVE 1 — KI-Belegerfassung */}
      <section className="relative z-[2] py-24 sm:py-32 bg-[#062417]" data-testid="deepdive-receipts-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Mockup */}
              <div className="min-w-0 overflow-hidden rounded-2xl border border-emerald-800/40 bg-green-950/40 backdrop-blur-xl p-8" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-emerald-700/50 rounded-xl p-6 text-center bg-emerald-500/5">
                    <div className="w-12 h-12 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Upload className="h-6 w-6 text-emerald-400" />
                    </div>
                    <p className="text-sm font-medium text-emerald-100">Beleg per Drag &amp; Drop hochladen</p>
                    <p className="text-xs text-emerald-100/50 mt-1">PDF, Fotos, Screenshots</p>
                  </div>
                  <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-600/40 rounded-lg px-3 py-2.5">
                    <span className="text-[10px] font-black bg-emerald-500 text-green-950 px-1.5 py-0.5 rounded">NEU</span>
                    <p className="text-xs text-emerald-100 truncate min-w-0 flex-1">…oder per E-Mail an <span className="font-mono font-medium text-emerald-300">max-a1b2@belege.ivoliq.io</span></p>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1 bg-green-950/60 rounded-lg p-3 border border-emerald-900/50">
                      <p className="text-xs text-emerald-100/40">Erkannt</p>
                      <p className="font-semibold text-sm text-white">Amazon AWS</p>
                      <p className="text-xs text-emerald-100/40 mt-1">29,00 € · 19% MwSt</p>
                    </div>
                    <div className="flex-1 bg-green-950/60 rounded-lg p-3 border border-emerald-900/50">
                      <p className="text-xs text-emerald-100/40">Kategorie</p>
                      <p className="font-semibold text-sm text-white">Software</p>
                      <p className="text-xs text-emerald-400 mt-1">100% absetzbar</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Text */}
              <div className="space-y-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-700/50 bg-emerald-500/10 px-4 py-1.5 text-sm font-semibold text-emerald-300">
                  <ScanLine className="h-4 w-4" /> KI-Belegerfassung
                  <span className="text-[10px] font-black bg-emerald-500 text-green-950 px-1.5 py-0.5 rounded-full">NEU: per E-Mail</span>
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">
                  <span className="font-light">Du leitest den Beleg weiter —</span>{' '}
                  <span className="font-black">ivoliq erledigt den Rest.</span>
                </h2>
                <p className="text-base md:text-lg text-emerald-50/70 leading-relaxed">
                  Belege einfach per E-Mail weiterleiten – unsere KI erledigt den Rest.
                  Oder fotografieren, oder per Drag &amp; Drop hochladen. Betrag, Händler, MwSt und Kategorie
                  werden automatisch erkannt — DSGVO-konform.
                </p>
                <ul className="space-y-3.5">
                  {[
                    <><strong className="text-white">NEU:</strong> Persönliche Beleg-E-Mail-Adresse – Rechnungen einfach weiterleiten</>,
                    'Drag & Drop oder Kamera-Upload',
                    'Automatische Erkennung aller Belegdaten inkl. Multi-MwSt',
                    'AfA-Vorschlag nach DACH-Recht (GWG-Prüfung)',
                    'ZIP + Excel Export für den Steuerberater',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-emerald-50/80">
                      <Check className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* DEEP DIVE — Zeit → Rechnung in einem Klick */}
      <section className="relative z-[2] py-24 sm:py-32" data-testid="deepdive-time-invoice-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Text */}
              <div className="space-y-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-700/50 bg-emerald-500/10 px-4 py-1.5 text-sm font-semibold text-emerald-300">
                  <Clock className="h-4 w-4" /> Zeiterfassung → Rechnung
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">
                  <span className="font-light">Stunden getrackt.</span>{' '}
                  <span className="font-black">Rechnung mit einem Klick.</span>
                </h2>
                <p className="text-base md:text-lg text-emerald-50/70 leading-relaxed">
                  Timer starten, arbeiten, stoppen. Am Monatsende wählst du deine offenen Stunden aus —
                  ivoliq macht daraus <strong className="text-white">in einem Klick</strong> eine rechtssichere
                  Rechnung mit Zahlungs-QR. Keine vergessenen Stunden, keine Tipparbeit.
                </p>
                <ul className="space-y-3.5">
                  {[
                    'One-Click-Timer mit Projekt- & Kundenzuordnung',
                    'Offene Stunden per Klick zur Rechnung bündeln',
                    'Stundensatz, Positionen & Steuersätze automatisch ausgefüllt',
                    'Zahlungs-QR drauf — dein Kunde zahlt per Scan',
                    'Mahnung direkt aus ivoliq, wenn die Zahlung mal dauert',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-emerald-50/80">
                      <Check className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Mockup: 3-Schritte-Flow */}
              <div className="rounded-2xl border border-emerald-800/40 bg-green-950/40 backdrop-blur-xl p-8" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                <div className="space-y-4">
                  {/* Schritt 1: Zeiteinträge */}
                  <div className="rounded-xl border border-emerald-900/50 bg-green-950/60 p-4">
                    <p className="text-xs text-emerald-100/40 mb-3 font-semibold uppercase tracking-wide">Deine Woche</p>
                    <div className="space-y-2.5">
                      {[
                        { p: 'Website-Relaunch · Müller GmbH', h: '6:30 h' },
                        { p: 'SEO-Beratung · Studio Nord', h: '3:15 h' },
                        { p: 'Wartung · Café Krone', h: '1:45 h' },
                      ].map(({ p, h }) => (
                        <div key={p} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="h-2 w-2 rounded-full bg-emerald-400 flex-shrink-0" />
                            <span className="text-emerald-50/80 truncate">{p}</span>
                          </div>
                          <span className="font-mono text-emerald-300 flex-shrink-0 ml-3">{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Schritt 2: 1-Klick-Button */}
                  <div className="flex items-center justify-center gap-3">
                    <div className="h-px flex-1 bg-emerald-900/60" />
                    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-green-950 shadow-[0_8px_24px_rgba(16,185,129,0.3)]">
                      <FileText className="h-4 w-4" /> Rechnung erstellen — 1 Klick
                    </div>
                    <div className="h-px flex-1 bg-emerald-900/60" />
                  </div>
                  {/* Schritt 3: fertige Rechnung */}
                  <div className="rounded-xl border border-emerald-700/40 bg-emerald-500/5 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs text-emerald-100/40 mb-1">Rechnung RE-2026-041</p>
                        <p className="font-semibold text-white text-sm">11:30 h · 3 Projekte</p>
                        <p className="font-heading text-2xl font-black text-emerald-400 mt-1.5">1.092,50 €</p>
                        <p className="text-xs text-emerald-100/40 mt-1">inkl. USt. · rechtssicher · versandfertig</p>
                      </div>
                      <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                        <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-emerald-700/40 bg-green-950/60">
                          <QrCode className="h-9 w-9 text-emerald-300" />
                        </div>
                        <span className="text-[10px] text-emerald-100/40">Zahlungs-QR</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 5. DEEP DIVE 2 — Steuern DACH mit Länder-Tabs */}
      <section className="relative z-[2] py-24 sm:py-32" data-testid="deepdive-tax-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-3xl mb-14">
              <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">
                <span className="font-light">Gebaut für</span>{' '}
                <span className="font-black">Deutschland, Österreich &amp; die Schweiz.</span>
              </h2>
              <p className="mt-4 text-base md:text-lg text-emerald-50/60 leading-relaxed">
                Nicht ein internationales Tool, das DACH „auch irgendwie" unterstützt.
                ivoliq kennt die Steuergesetze, Pflichtangaben und Besonderheiten jedes Landes.
              </p>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <div className="flex flex-wrap gap-3 mb-10">
              {countries.map(({ code, flag, name }) => (
                <button
                  key={code}
                  onClick={() => setActiveCountry(code)}
                  data-testid={`country-tab-${code}`}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold ${
                    activeCountry === code
                      ? 'bg-emerald-500 text-green-950 shadow-[0_8px_24px_rgba(16,185,129,0.3)]'
                      : 'bg-green-950/40 text-emerald-50/70 border border-emerald-900/50 hover:border-emerald-700/60'
                  }`}
                  style={{ transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease' }}
                >
                  <span className="text-xl">{flag}</span> {name}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-6 md:gap-8" data-testid="country-features-grid">
              {countryFeatures[activeCountry].map((f) => (
                <div key={f.t} className="rounded-2xl border border-emerald-900/50 bg-green-950/20 p-8" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.25)' }}>
                  <h4 className="font-heading font-bold text-white text-lg">{f.t}</h4>
                  <p className="mt-2 text-sm text-emerald-50/60 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* SB-Call-out */}
      <section className="relative z-[2] py-16" data-testid="sb-callout-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <Link to="/steuerberater-portal" data-testid="landing-sb-portal-link" className="block group">
              <div className="relative overflow-hidden rounded-2xl border border-emerald-700/50 bg-[#062417] p-8 sm:p-10 hover:border-emerald-500/60" style={{ transition: 'border-color 0.3s ease', boxShadow: '0 8px 40px rgba(0,0,0,0.35)' }}>
                <div className="pointer-events-none absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -translate-y-1/2 translate-x-1/4"></div>
                <div className="relative grid md:grid-cols-[auto_1fr_auto] items-center gap-6">
                  <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-700/40 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Handshake className="h-8 w-8 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-1">
                      Für Steuerberater &amp; Kanzleien
                    </div>
                    <h3 className="font-heading text-2xl sm:text-3xl font-black text-white mb-1">
                      Sie betreuen Freelancer-Mandanten?
                    </h3>
                    <p className="text-emerald-50/60 text-base">
                      Lernen Sie das ivoliq Steuerberater-Portal in 30 Min kennen – kostenlos &amp; unverbindlich.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500 group-hover:bg-emerald-400 text-green-950 px-6 py-3.5 font-bold text-base" style={{ transition: 'background-color 0.2s ease' }}>
                      Zum Partner-Portal
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1" style={{ transition: 'transform 0.2s ease' }} />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* 6. PRICING */}
      <section id="pricing" className="relative z-[2] py-24 sm:py-32" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="inline-flex items-center rounded-full border border-emerald-700/50 bg-emerald-500/10 text-emerald-300 px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
              Preise &amp; Pakete
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-white tracking-tight mb-4" data-testid="landing-pricing-title">
              <span className="font-light">Wähle</span> <span className="font-black">dein ivoliq.</span>
            </h2>
            <p className="text-base md:text-lg text-emerald-50/60 leading-relaxed">
              Kostenlos starten. Wachsen, wenn du es brauchst. Lifetime: <strong className="text-emerald-300">Einmal zahlen, für immer nutzen.</strong>
            </p>
          </div>

          {/* Heller Pricing-Container: PricingTiersBlock ist für hellen Grund gestaltet */}
          <div className="landing-tracing-beam rounded-3xl bg-zinc-50 p-4 sm:p-8 md:p-10" style={{ boxShadow: '0 16px 64px rgba(0,0,0,0.45)' }}>
            {plansData ? (
              <PricingTiersBlock
                plans={plansData}
                billingInterval={billingInterval}
                onBillingChange={setBillingInterval}
                onSelectPlan={(planId, tier) => {
                  const price = tier === 'free' ? 0
                    : tier === 'basic' ? (billingInterval === 'yearly' ? 90 : 9)
                    : (billingInterval === 'yearly' ? 190 : 19);
                  trackPricingClick(tier.charAt(0).toUpperCase() + tier.slice(1), price);
                  window.location.href = `/signup?plan=${planId}`;
                }}
                onLifetime={lifetimeBundleInfo && (lifetimeBundleInfo.sold_count || 0) < 150 ? () => {
                  trackPricingClick('Lifetime', 199);
                  window.location.href = '/signup?plan=lifetime&intent=lifetime';
                } : null}
                showLifetime={!!lifetimeBundleInfo && (lifetimeBundleInfo.sold_count || 0) < 150}
                lifetimePrice={lifetimeBundleInfo ? (
                  (lifetimeBundleInfo.sold_count || 0) < 50 ? '199' :
                  (lifetimeBundleInfo.sold_count || 0) < 150 ? '249' : '199'
                ) : '199'}
                lifetimeBadge={lifetimeBundleInfo ? (
                  (lifetimeBundleInfo.sold_count || 0) < 50 ? `Nur noch ${50 - (lifetimeBundleInfo.sold_count || 0)} verfügbar` :
                  (lifetimeBundleInfo.sold_count || 0) < 150 ? `Nur noch ${150 - (lifetimeBundleInfo.sold_count || 0)} verfügbar` : null
                ) : null}
                lifetimeSoldOut={lifetimeBundleInfo ? (lifetimeBundleInfo.sold_count || 0) >= 150 : false}
              />
            ) : (
              <div className="text-center py-16 text-zinc-400">Lade Tarife…</div>
            )}
          </div>
        </div>
      </section>

      {/* 7. FAQ */}
      <section id="faq" className="relative z-[2] py-24 bg-[#062417]" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-12">
            <span className="font-light">Häufig gestellte</span> <span className="font-black">Fragen.</span>
          </h2>
          <div className="space-y-4">
            {faqItems.map((item) => (
              <div key={item.id} className="rounded-xl border border-emerald-900/50 bg-green-950/30 overflow-hidden">
                <button
                  onClick={() => toggleFaq(item.id, item.q)}
                  className="w-full px-6 py-5 text-left flex justify-between items-center gap-4 hover:bg-emerald-500/5"
                  style={{ transition: 'background-color 0.2s ease' }}
                  data-testid={item.testid || `faq-toggle-${item.id}`}
                >
                  <span className="font-semibold text-white text-base sm:text-lg">{item.q}</span>
                  <ChevronDown className={`h-5 w-5 text-emerald-400 flex-shrink-0 ${openFaq === item.id ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s ease' }} />
                </button>
                {openFaq === item.id && (
                  <div className="px-6 pb-5 text-emerald-50/70 leading-relaxed">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO INTERNAL LINKS */}
      <section className="relative z-[2] py-20" data-testid="seo-tools-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl text-white tracking-tight">
              <span className="font-light">Kostenlose</span> <span className="font-black">Rechner & Guides für Freelancer.</span>
            </h2>
            <p className="mt-3 text-emerald-50/50">
              Rechnung schreiben, Steuern zurücklegen, Stundensatz kalkulieren, Tools vergleichen — ohne Anmeldung.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { to: '/rechnung-erstellen-kostenlos-deutschland', icon: FileText, t: 'Rechnung erstellen Deutschland', d: 'Kostenlose Rechnungserstellung für Deutschland mit deutscher Umsatzsteuer' },
              { to: '/rechnung-erstellen-kostenlos-oesterreich', icon: FileText, t: 'Rechnung erstellen Österreich', d: 'Kostenlose Rechnungserstellung für Österreich mit österreichischer Umsatzsteuer' },
              { to: '/qr-rechnung-erstellen-kostenlos', icon: FileText, t: 'Rechnung erstellen Schweiz', d: 'Kostenlose Rechnungserstellung für die Schweiz mit Schweizer Mehrwertsteuer' },
              { to: '/steuerruecklagen-freelancer-deutschland', icon: Calculator, t: 'Steuerrücklagen Deutschland', d: 'Berechne deine Steuerrücklagen für Deutschland inkl. Einkommensteuer und Umsatzsteuer' },
              { to: '/steuerruecklagen-freelancer-oesterreich', icon: Calculator, t: 'Steuerrücklagen Österreich', d: 'Berechne deine Steuerrücklagen für Österreich inkl. Einkommensteuer und SVS' },
              { to: '/steuerruecklagen-freelancer-schweiz', icon: Calculator, t: 'Steuerrücklagen Schweiz', d: 'Berechne deine Steuerrücklagen für die Schweiz inkl. Bundessteuer und Kantonalsteuer' },
              { to: '/stundensatz-rechner', icon: Calculator, t: 'Stundensatz-Rechner', d: 'Mindest-Stundensatz aus Wunschgehalt, Abgaben und Auslastung — mit anpassbaren Annahmen für DE, AT und CH' },
              { to: '/zeiterfassung-freelancer', icon: Clock, t: 'Zeiterfassung für Freelancer', d: 'Guide, Vorlage und Tool-Vergleich: So wird aus erfassten Stunden mit einem Klick die Rechnung' },
              { to: '/lexoffice-alternative', icon: Scale, t: 'lexoffice-Vergleich', d: 'ivoliq vs. lexoffice und sevDesk: Funktionen, Preise und Umzug in 5 Schritten für Freelancer' },
            ].map(({ to, icon: I, t, d }) => (
              <Link
                key={to}
                to={to}
                data-testid={`seo-tool-card-${to.slice(1)}`}
                className="group p-6 rounded-xl border border-emerald-900/50 bg-green-950/20 hover:border-emerald-700/60 hover:-translate-y-0.5"
                style={{ transition: 'border-color 0.2s ease, transform 0.2s ease' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-800/40">
                    <I className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-white group-hover:text-emerald-300" style={{ transition: 'color 0.2s ease' }}>{t}</h3>
                </div>
                <p className="text-sm text-emerald-50/50">{d}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 8. FINAL CTA — massiv */}
      <section className="relative z-[2] py-28 sm:py-36 overflow-hidden" data-testid="final-cta-section">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[400px] bg-emerald-500/10 blur-3xl rounded-full translate-y-1/2" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-heading text-4xl sm:text-6xl text-white tracking-tight leading-[1.05] sm:leading-[1.02] mb-8 break-words">
            <span className="font-light">Lass uns</span> <span className="font-black text-emerald-400">starten.</span>
          </h2>
          <p className="text-lg sm:text-xl text-emerald-50/60 mb-12 max-w-2xl mx-auto">
            Starte jetzt kostenlos — in 2 Minuten startklar, jederzeit kündbar.
            Oder lass dir ivoliq in 30 Minuten persönlich zeigen.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup" onClick={() => trackCTAClick('final_cta', 'Kostenlos starten')} data-testid="final-cta-signup-link">
              <Button
                size="lg"
                className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-green-950 font-bold px-12 py-8 text-xl shadow-[0_8px_40px_rgba(16,185,129,0.4)] hover:-translate-y-0.5"
                style={{ transition: 'background-color 0.2s ease, transform 0.2s ease' }}
                data-testid="final-cta-signup-button"
              >
                Kostenlos starten
              </Button>
            </Link>
            <Button
              size="lg"
              className="rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold px-10 py-8 text-xl backdrop-blur-md border border-white/10"
              style={{ transition: 'background-color 0.2s ease' }}
              onClick={() => { trackCTAClick('final_cta', 'Demo buchen'); openCalModal(); }}
              data-testid="final-cta-demo-button"
            >
              <Video className="h-5 w-5 mr-2" />Demo buchen
            </Button>
          </div>
          <p className="text-sm text-emerald-100/40 mt-8">
            ✓ Free Plan ohne Kreditkarte &nbsp;·&nbsp; ✓ 30 Tage Pro kostenlos testen &nbsp;·&nbsp; ✓ 30-Min-Demo unverbindlich
          </p>
        </div>
      </section>

      <div className="relative z-[2]">
        <Footer />
      </div>

      {/* Exit-Intent: letztes Lifetime-Angebot */}
      <ExitIntentOffer
        lifetimeBundleInfo={lifetimeBundleInfo}
        trackEvent={trackEvent}
        trackPricingClick={trackPricingClick}
      />
    </div>
  );
}
